import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef, useState } from 'react';
import { Play, Instagram, Facebook, Linkedin } from 'lucide-react';

// Custom TikTok icon component
const TikTok = ({ size = 24, className = '' }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="currentColor" 
    className={className}
  >
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const Testimonials = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.05 });
  const [activeTab, setActiveTab] = useState('studio-builds');

  const serviceTabs = [
    { id: 'studio-builds', label: 'Studio Builds' },
    { id: 'broadcast-partner', label: 'Broadcast Partner' },
    { id: 'live-production', label: 'Live Production' },
    { id: 'video-production', label: 'Video Production' },
    { id: 'virtual-events', label: 'Virtual Events' },
  ];

  // Videos organized by service type
  const serviceVideos = {
    'studio-builds': [
      {
        id: 1,
        title: 'Studio Build Testimonial',
        videoUrl: 'https://customer-assets.emergentagent.com/job_faith-production-1/artifacts/xmdikxj7_Test%20Vid%202.mp4',
        thumbnail: null,
      }
    ],
    'broadcast-partner': [],
    'live-production': [],
    'video-production': [],
    'virtual-events': [],
  };

  const socialLinks = [
    { icon: Instagram, href: 'https://www.instagram.com/kingdomvibestudios', label: 'Instagram' },
    { icon: Facebook, href: 'https://www.facebook.com/share/1AvPsnck2i/', label: 'Facebook' },
    { icon: Linkedin, href: 'https://www.linkedin.com/in/brian-b-b6077a3ab', label: 'LinkedIn' },
    { icon: TikTok, href: 'https://www.tiktok.com/@kingdom.v.i.b.e.s', label: 'TikTok' },
  ];

  const ComingSoonPlaceholder = () => (
    <div className="relative aspect-video bg-obsidian border border-amber-400/30 flex flex-col items-center justify-center group">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-amber-400/5 via-transparent to-neon-pink/5" />
      
      {/* Pulsing glow effect */}
      <motion.div
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(circle at 50% 50%, rgba(251, 191, 36, 0.1) 0%, transparent 50%)',
        }}
        animate={{
          opacity: [0.3, 0.6, 0.3],
          scale: [1, 1.05, 1],
        }}
        transition={{
          duration: 3,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />
      
      {/* Crown logo */}
      <motion.img 
        src="https://customer-assets.emergentagent.com/job_7622ca65-87c5-425f-ac54-f38f9958c6c6/artifacts/zd4sl0r2_Logo%202.png"
        alt="Kingdom V.I.B.E. Studios" 
        className="h-24 sm:h-32 md:h-40 mx-auto relative z-10 opacity-60"
        animate={{
          opacity: [0.5, 0.7, 0.5],
        }}
        transition={{
          duration: 3,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />
      
      {/* Coming Soon text */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="relative z-10 mt-4 sm:mt-6 text-center"
      >
        <h3 
          className="font-heading text-xl sm:text-2xl md:text-3xl font-bold uppercase text-amber-400"
          style={{ textShadow: '0 0 20px rgba(251, 191, 36, 0.4)' }}
        >
          Coming Soon
        </h3>
        <p className="text-white/50 text-sm sm:text-base mt-2">
          Testimonials for this service are on the way
        </p>
      </motion.div>
    </div>
  );

  const VideoPlayer = ({ video }) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const videoRef = useRef(null);

    const handlePlay = () => {
      if (videoRef.current) {
        if (isPlaying) {
          videoRef.current.pause();
        } else {
          videoRef.current.play();
        }
        setIsPlaying(!isPlaying);
      }
    };

    return (
      <div className="relative aspect-video bg-obsidian border border-amber-400/30 overflow-hidden group">
        <video
          ref={videoRef}
          src={video.videoUrl}
          className="w-full h-full object-cover"
          controls={isPlaying}
          onEnded={() => setIsPlaying(false)}
          onPause={() => setIsPlaying(false)}
          onPlay={() => setIsPlaying(true)}
        />
        
        {/* Play button overlay - shows when not playing */}
        {!isPlaying && (
          <div 
            className="absolute inset-0 flex items-center justify-center cursor-pointer bg-black/30 hover:bg-black/20 transition-colors"
            onClick={handlePlay}
          >
            <div className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 rounded-full bg-amber-400/20 border-2 border-amber-400 flex items-center justify-center hover:bg-amber-400/30 hover:scale-110 transition-all">
              <Play className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 text-amber-400 ml-1" fill="currentColor" />
            </div>
          </div>
        )}
      </div>
    );
  };

  const currentVideos = serviceVideos[activeTab] || [];

  return (
    <section id="testimonials" className="relative py-16 sm:py-20 md:py-24 lg:py-32 bg-obsidian-paper overflow-hidden" data-testid="testimonials-section" ref={ref}>
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-neon-pink/50 to-transparent" />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center max-w-3xl mx-auto mb-8 sm:mb-10 md:mb-12"
        >
          <span className="font-mono text-sm sm:text-base uppercase tracking-widest text-white mb-3 sm:mb-4 block">
            Results
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold uppercase tracking-tight mb-3 sm:mb-4" data-testid="testimonials-heading">
            Trusted By <span className="text-neon-pink" style={{ textShadow: '0 0 20px rgba(255, 0, 153, 0.4)' }}>Faith-Based Leaders</span>
          </h2>
          <p className="text-white/60 text-sm sm:text-base">
            See what our clients have to say about working with Kingdom V.I.B.E. Studios
          </p>
        </motion.div>

        {/* Service Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mb-8 sm:mb-10"
        >
          <div className="flex flex-wrap justify-center gap-2 sm:gap-3">
            {serviceTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-3 sm:px-4 md:px-6 py-2 sm:py-3 font-heading text-xs sm:text-sm uppercase tracking-wider transition-all duration-300 border ${
                  activeTab === tab.id
                    ? 'bg-amber-400 text-obsidian border-amber-400'
                    : 'bg-transparent text-white/70 border-white/20 hover:border-amber-400/50 hover:text-amber-400'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Video Content */}
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-4xl mx-auto mb-8 sm:mb-10 md:mb-12"
        >
          {currentVideos.length > 0 ? (
            <div className="grid gap-6">
              {currentVideos.map((video) => (
                <VideoPlayer key={video.id} video={video} />
              ))}
            </div>
          ) : (
            <ComingSoonPlaceholder />
          )}
        </motion.div>

        {/* See More Work + Social Icons */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="text-center"
        >
          <p className="text-white/60 text-sm sm:text-base mb-3 sm:mb-4">See more of our work</p>
          <div className="flex justify-center gap-3 sm:gap-4">
            {socialLinks.map((social, index) => (
              <a
                key={index}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 sm:w-12 sm:h-12 border border-white/20 flex items-center justify-center text-white/60 hover:text-amber-400 hover:border-amber-400 transition-all"
                aria-label={social.label}
              >
                <social.icon size={16} className="sm:w-5 sm:h-5" />
              </a>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Testimonials;
